import React, { useEffect, useRef, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { fetchMateriaalTypes } from "./services/projectDetailService";

// 27/10/2026 (Bartel, workflow "dringende interventie"): materialen als lijst (omschrijving, aantal,
// eenheid), zodat ze in de werkbon en in het ingevulde mandaat als tabel komen. Onderaan staat altijd
// een lege regel; zodra de werker erin typt, wordt het een echte regel en komt er een nieuwe lege bij
// (geen knop "Toevoegen"). De omschrijving kiest uit de keuzelijst van de administratie
// (materiaal_types/) maar blijft vrij invulbaar; de eenheid vult zich in vanuit het gekozen type.
// Lege regels worden bij het versturen weggelaten.

let keyCounter = 1;
const newKey = () => `m${keyCounter++}`;

export const withKeys = (list) =>
    (list || []).map((item) => ({
        omschrijving: item.omschrijving || "",
        aantal: item.aantal === null || item.aantal === undefined ? "" : String(item.aantal),
        eenheid: item.eenheid || "",
        opmerking: item.opmerking || "",
        _key: item._key || newKey(),
    }));

// Voor de backend: lege regels weg, aantal als getal (komma mag), sleutel weg.
export const materialenForSubmit = (list) =>
    (list || [])
        .filter((item) => (item.omschrijving || "").trim())
        .map((item) => {
            const raw = String(item.aantal ?? "").replace(",", ".").trim();
            const aantal = raw === "" || isNaN(Number(raw)) ? null : Number(raw);
            return {
                omschrijving: item.omschrijving.trim(),
                aantal,
                eenheid: (item.eenheid || "").trim(),
                opmerking: item.opmerking || "",
            };
        });

const MaterialenLijst = ({ materialen, onChange }) => {
    const { t } = useLingui();
    const [types, setTypes] = useState([]);
    const [typesError, setTypesError] = useState(false);
    const blankKey = useRef(newKey());
    const rows = materialen || [];

    useEffect(() => {
        let cancelled = false;
        fetchMateriaalTypes()
            .then((list) => { if (!cancelled) setTypes(list); })
            .catch((e) => {
                console.error("Error fetching materiaal types:", e);
                if (!cancelled) setTypesError(true);
            });
        return () => { cancelled = true; };
    }, []);

    const unitFor = (omschrijving) => {
        const match = types.find((type) => type.naam.toLowerCase() === (omschrijving || "").trim().toLowerCase());
        return match ? match.eenheid : null;
    };

    const update = (key, field, value) => {
        const existing = rows.find((row) => row._key === key);
        let next;
        if (existing) {
            next = rows.map((row) => (row._key === key ? { ...row, [field]: value } : row));
        } else {
            // De lege regel onderaan wordt een echte regel; dezelfde sleutel, zodat de focus blijft.
            next = [...rows, { omschrijving: "", aantal: "", eenheid: "", opmerking: "", _key: key, [field]: value }];
            blankKey.current = newKey();
        }
        if (field === "omschrijving") {
            const unit = unitFor(value);
            if (unit) next = next.map((row) => (row._key === key && !row.eenheid ? { ...row, eenheid: unit } : row));
        }
        onChange(next);
    };

    const remove = (key) => onChange(rows.filter((row) => row._key !== key));

    const allRows = [...rows, { omschrijving: "", aantal: "", eenheid: "", _key: blankKey.current, blank: true }];

    return (
        <div className="materialen-lijst">
            <datalist id="materiaal-types">
                {types.map((type) => (
                    <option key={type.id} value={type.naam} />
                ))}
            </datalist>
            <div className="materialen-rij materialen-kop">
                <span><Trans>Description</Trans></span>
                <span><Trans>Quantity</Trans></span>
                <span><Trans>Unit</Trans></span>
                <span />
            </div>
            {allRows.map((row) => (
                <div key={row._key} className="materialen-rij">
                    <input
                        type="text"
                        list="materiaal-types"
                        value={row.omschrijving}
                        placeholder={row.blank ? t`New material...` : ""}
                        onChange={(e) => update(row._key, "omschrijving", e.target.value)}
                    />
                    <input
                        type="text"
                        inputMode="decimal"
                        value={row.aantal}
                        onChange={(e) => update(row._key, "aantal", e.target.value)}
                    />
                    <input
                        type="text"
                        value={row.eenheid}
                        maxLength={20}
                        onChange={(e) => update(row._key, "eenheid", e.target.value)}
                    />
                    {row.blank ? (
                        <span />
                    ) : (
                        <button type="button" className="materialen-weg" onClick={() => remove(row._key)} aria-label="remove">
                            ✖
                        </button>
                    )}
                </div>
            ))}
            {typesError && (
                <p className="signed-copy-error">
                    <Trans>The material list could not be loaded; you can still type freely.</Trans>
                </p>
            )}
        </div>
    );
};

export default MaterialenLijst;
